import type { ChatMessage } from '../components/chat';
import { talentList } from '../pages/merchant/talentSquare/talentSquareData';
import type { EmailAccount, EmailConversation, EmailService } from './emailService';

type MockThread = { conversation: EmailConversation; messages: ChatMessage[] };

function at(daysAgo: number, time: string) {
  const [h, m] = time.split(':').map(Number);
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

function mail(
  id: string,
  direction: ChatMessage['direction'],
  createdAt: string,
  subject: string,
  content: string,
  status?: ChatMessage['status'],
): ChatMessage {
  return { id, direction, contentType: 'text', subject, content, createdAt, status };
}

function summarize(conversation: EmailConversation, messages: ChatMessage[]): EmailConversation {
  const last = messages[messages.length - 1];
  return {
    ...conversation,
    lastMessagePreview: last?.content ?? '',
    lastMessageAt: last?.createdAt ?? conversation.lastMessageAt,
    lastMessageDirection: last?.direction ?? 'out',
    sentCount: messages.filter((item) => item.direction === 'out').length,
    receivedCount: messages.filter((item) => item.direction === 'in').length,
    openedCount: messages.filter((item) => item.direction === 'out' && item.status === 'read').length,
  };
}

function thread(
  conversation: Omit<
    EmailConversation,
    'lastMessagePreview' | 'lastMessageAt' | 'lastMessageDirection' | 'sentCount' | 'receivedCount' | 'openedCount'
  >,
  messages: ChatMessage[],
): MockThread {
  const base: EmailConversation = {
    ...conversation,
    lastMessagePreview: '',
    lastMessageAt: '',
    lastMessageDirection: 'out',
    sentCount: 0,
    receivedCount: 0,
    openedCount: 0,
  };
  return { conversation: summarize(base, messages), messages };
}

function createThreads(): MockThread[] {
  return [
    thread(
      {
        id: 'e-linh-tran',
        talentId: 'linh-tran',
        peer: { name: 'Linh Tran', initial: 'L', color: '#596ae8' },
        handle: '@linh.glow',
        email: 'linh.glow@example.com',
        region: '越南',
        category: '美妆',
        subject: '新品试样合作',
        unreadCount: 1,
        intent: true,
        bounced: false,
      },
      [
        mail(
          'linh-m1',
          'out',
          at(3, '11:02'),
          '新品试样合作',
          'Hi Linh，诚邀你试用 VN-BeautyHub 维C精华，佣金 18%，也可走定向邀约链接。',
          'read',
        ),
        mail('linh-m2', 'in', at(2, '09:20'), 'Re: 新品试样合作', '可以先寄样品，地址今晚发你。'),
        mail(
          'linh-m3',
          'out',
          at(2, '10:05'),
          'Re: 新品试样合作',
          '好的，样品今天寄出。签收后方便把链接挂上橱窗。',
          'read',
        ),
        mail('linh-m4', 'in', at(0, '14:08'), 'Re: 新品试样合作', '样品收到了，这周可以拍短视频。收件地址已更新，佣金按今晚处理。'),
      ],
    ),
    thread(
      {
        id: 'e-ploy-siri',
        talentId: 'ploy-siri',
        peer: { name: 'Ploy Siri', initial: 'P', color: '#8b5cf6' },
        handle: '@ployskin',
        email: 'ploy.siri@example.com',
        region: '泰国',
        category: '个护',
        subject: '身体乳合作邀请',
        unreadCount: 1,
        intent: false,
        bounced: false,
      },
      [
        mail('ploy-m1', 'out', at(1, '16:10'), '身体乳合作邀请', 'Hi Ploy，新款身体乳想邀请你试用，佣金 15%。', 'read'),
        mail('ploy-m2', 'in', at(0, '11:41'), 'Re: 身体乳合作邀请', '如果佣金到 22% 我可以先拍一条试试看。'),
      ],
    ),
    thread(
      {
        id: 'e-mei-tan',
        talentId: 'mei-tan',
        peer: { name: 'Mei Tan', initial: 'M', color: '#f59e0b' },
        handle: '@meipicks',
        email: 'mei.tan@example.com',
        region: '马来西亚',
        category: '美妆',
        subject: '视频发布排期',
        unreadCount: 0,
        intent: true,
        bounced: false,
      },
      [
        mail('mei-m1', 'out', at(2, '10:00'), '视频发布排期', 'Mei 你好，视频预计哪天发布？我们可以同步投流。', 'read'),
        mail('mei-m2', 'in', at(1, '20:15'), 'Re: 视频发布排期', '视频已发布，广告投放保持到周末。'),
        mail('mei-m3', 'out', at(0, '09:22'), 'Re: 视频发布排期', '收到，数据很好，辛苦啦！', 'delivered'),
      ],
    ),
    thread(
      {
        id: 'e-jade-nguyen',
        talentId: 'jade-nguyen',
        peer: { name: 'Jade Nguyen', initial: 'J', color: '#6366f1' },
        handle: '@jade.fits',
        email: 'jade.fits@example.com',
        region: '越南',
        category: '女装',
        subject: '样品寄送通知',
        unreadCount: 0,
        intent: false,
        bounced: false,
      },
      [
        mail('jade-m1', 'out', at(1, '16:30'), '样品寄送通知', '样品已寄出，物流单号 VN2024091001，激活文案见附件。', 'delivered'),
      ],
    ),
    thread(
      {
        id: 'e-rina-lim',
        talentId: 'rina-lim',
        peer: { name: 'Rina Lim', initial: 'R', color: '#0ea5e9' },
        handle: '@rina.tech',
        email: 'rina.lim@example.com',
        region: '新加坡',
        category: '3C',
        subject: '3C 新款合作',
        unreadCount: 0,
        intent: false,
        bounced: false,
      },
      [
        mail('rina-m1', 'out', at(4, '10:00'), '3C 新款合作', 'Hi Rina，3C 新款耳机可否安排一期测评？佣金 12%。', 'read'),
      ],
    ),
    thread(
      {
        id: 'e-ayu-putri',
        talentId: 'ayu-putri',
        peer: { name: 'Ayu Putri', initial: 'A', color: '#94a3b8' },
        handle: '@ayu.daily',
        email: 'ayu.putri@example.com',
        region: '印尼',
        category: '家居',
        subject: '家居好物合作',
        unreadCount: 0,
        intent: false,
        bounced: true,
      },
      [
        mail('ayu-m1', 'out', at(34, '20:04'), '家居好物合作', 'Hi Ayu，想邀请你体验我们的家居收纳系列。', 'failed'),
      ],
    ),
  ];
}

function delay(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

export function createMockEmailService(latencyMs = 250): EmailService {
  const threads = createThreads();
  const account: EmailAccount = {
    platformAddress: 'u1024@mail.wowbit.io',
    monthlySent: 1842,
    monthlyLimit: 3000,
    deliveryRate: 97.1,
  };

  const findThread = (id: string) => {
    const found = threads.find((item) => item.conversation.id === id);
    if (!found) {
      throw new Error(`Unknown email conversation: ${id}`);
    }
    return found;
  };

  const append = (target: MockThread, message: ChatMessage) => {
    target.messages.push(message);
    target.conversation = summarize(target.conversation, target.messages);
    account.monthlySent += 1;
  };

  return {
    async listConversations() {
      await delay(latencyMs);
      return threads.map((item) => ({ ...item.conversation }));
    },

    async listMessages(conversationId) {
      await delay(latencyMs);
      return findThread(conversationId).messages.slice();
    },

    async sendReply({ conversationId, clientId, content }) {
      await delay(latencyMs);
      const target = findThread(conversationId);
      const message: ChatMessage = {
        id: `mail-${Date.now().toString(36)}`,
        clientId,
        direction: 'out',
        contentType: 'text',
        subject: `Re: ${target.conversation.subject}`,
        content,
        createdAt: new Date().toISOString(),
        status: 'delivered',
      };
      append(target, message);
      target.conversation = { ...target.conversation, unreadCount: 0 };
      return message;
    },

    async sendNew({ talentIds, subject, content }) {
      await delay(latencyMs);
      let sent = 0;
      let skipped = 0;
      talentIds.forEach((talentId, index) => {
        let target = threads.find((item) => item.conversation.talentId === talentId);
        if (!target) {
          const talent = talentList.find((item) => item.id === talentId);
          if (!talent || !talent.hasEmail) {
            skipped += 1;
            return;
          }
          target = thread(
            {
              id: `e-${talent.id}`,
              talentId: talent.id,
              peer: { name: talent.name, initial: talent.initial, color: talent.color },
              handle: talent.handle,
              email: `${talent.handle.replace('@', '')}@example.com`,
              region: talent.country.split(' ')[0],
              category: talent.category.split(' ')[0],
              subject,
              unreadCount: 0,
              intent: false,
              bounced: false,
            },
            [],
          );
          threads.push(target);
        }
        if (target.conversation.bounced) {
          skipped += 1;
          return;
        }
        const personalized = content.replace(/\{昵称\}/g, target.conversation.peer.name);
        append(target, {
          id: `mail-${Date.now().toString(36)}-${index}`,
          direction: 'out',
          contentType: 'text',
          subject,
          content: personalized,
          createdAt: new Date().toISOString(),
          status: 'delivered',
        });
        sent += 1;
      });
      return { sent, skipped };
    },

    async markRead(conversationId) {
      await delay(latencyMs / 2);
      const target = findThread(conversationId);
      target.conversation = { ...target.conversation, unreadCount: 0 };
    },

    async setIntent(conversationId, intent) {
      await delay(latencyMs / 2);
      const target = findThread(conversationId);
      target.conversation = { ...target.conversation, intent };
    },

    async getAccount() {
      await delay(latencyMs / 2);
      return { ...account };
    },

    async bindAddress(address) {
      await delay(latencyMs);
      account.boundAddress = address;
    },

    async syncTalents() {
      await delay(latencyMs * 2);
      return talentList.filter((item) => item.hasEmail).length;
    },
  };
}
